import React, { useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState } from "@/hq/HQPrimitives";
import ServiceMultiSelect from "@/hq/clients/ServiceMultiSelect";
import { toast } from "sonner";
import { Play, Pause, CheckCircle2, XCircle, Trash2, RotateCcw, Loader2 } from "lucide-react";

const STATUS_TONE = {
  interested: { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  active:     { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  paused:     { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
  completed:  { fg: "#2E7CF6", bg: "#F0F7FF", bd: "#D6E7FF" },
  cancelled:  { fg: "#C1350B", bg: "#FFECEC", bd: "#FCE1C7" },
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.interested;
  return (
    <span
      data-testid={`service-status-${status}`}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.14em] uppercase"
      style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}
    >
      {status}
    </span>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch { return "—"; }
}

function ActionIcon({ Icon, testId, title, onClick, disabled, tone }) {
  const bg = { orange: "text-[#FF6A00] hover:bg-[#FFF7EE]",
               green:  "text-[#0FA958] hover:bg-[#EBFBF2]",
               grey:   "text-[#4A4A4A] hover:bg-[#F2F2F2]",
               blue:   "text-[#2E7CF6] hover:bg-[#F0F7FF]",
               red:    "text-[#C1350B] hover:bg-[#FFECEC]" }[tone] || "text-[#4A4A4A] hover:bg-[#F2F2F2]";
  return (
    <button
      onClick={onClick} disabled={disabled}
      data-testid={testId} title={title}
      className={`p-1.5 rounded-full ${bg} disabled:opacity-40`}
    >
      <Icon size={14}/>
    </button>
  );
}

/**
 * ClientServicesTab — Phase 2A.
 *
 * Renders the full Services table on the Client Profile.
 * Columns: SERVICE · STATUS · DATE ADDED · DATE ACTIVATED · ACTIONS.
 * Actions: activate, pause, complete, cancel, revert-to-interested,
 * delete (only when INTERESTED).
 *
 * Uses the same /api/hq/control/clients/{id}/services endpoints as the
 * Clients table — one structured source of truth.
 */
export default function ClientServicesTab({ businessId, slots, onChange }) {
  const [busyId, setBusyId] = useState(null);

  const patchStatus = async (bs_id, status) => {
    setBusyId(bs_id);
    try {
      const r = await hqApi.patch(
        `/control/clients/${businessId}/services/${bs_id}`,
        { status },
      );
      onChange?.(r.data.items || []);
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const removeRow = async (bs_id) => {
    setBusyId(bs_id);
    try {
      const r = await hqApi.delete(`/control/clients/${businessId}/services/${bs_id}`);
      onChange?.(r.data.items || []);
      toast.success("Service removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-6" data-testid="hq-client-services">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Services</div>
          <p className="text-sm text-[#4A4A4A] mt-1">
            Selecting a service marks it as <span className="font-bold">interested</span>. Activate it below when the client signs on.
          </p>
        </div>
        <div className="min-w-[280px]">
          <ServiceMultiSelect businessId={businessId} slots={slots} onChange={onChange}/>
        </div>
      </div>

      {(slots || []).length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title="No services selected"
            hint="Use the picker above to add Website, CRM, Landing Page or Orange Optimise."
            testId="hq-client-services-empty"
          />
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="hq-client-services-table">
            <thead className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A] bg-[#F6F5F2]">
              <tr>
                <th className="text-left px-3 py-3">Service</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Date added</th>
                <th className="text-left px-3 py-3">Date activated</th>
                <th className="text-right px-3 py-3 w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => {
                const st = s.status || "interested";
                const busy = busyId === s.bs_id;
                return (
                  <tr key={s.bs_id} className="border-t border-[#EAEAEA]"
                      data-testid={`hq-client-service-row-${s.service_key}`}>
                    <td className="px-3 py-3 font-black">{s.label}</td>
                    <td className="px-3 py-3"><StatusPill status={st}/></td>
                    <td className="px-3 py-3 text-[#4A4A4A]">{fmtDate(s.created_at)}</td>
                    <td className="px-3 py-3 text-[#4A4A4A]">{fmtDate(s.activated_at)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {busy && <Loader2 size={13} className="animate-spin text-[#8A8A8A] mr-1"/>}
                        {st === "interested" && (
                          <>
                            <ActionIcon Icon={Play} tone="green"
                              testId={`hq-client-service-activate-${s.service_key}`}
                              title="Activate"
                              onClick={() => patchStatus(s.bs_id, "active")} disabled={busy}/>
                            <ActionIcon Icon={XCircle} tone="red"
                              testId={`hq-client-service-cancel-${s.service_key}`}
                              title="Cancel"
                              onClick={() => patchStatus(s.bs_id, "cancelled")} disabled={busy}/>
                            <ActionIcon Icon={Trash2} tone="grey"
                              testId={`hq-client-service-delete-${s.service_key}`}
                              title="Remove"
                              onClick={() => removeRow(s.bs_id)} disabled={busy}/>
                          </>
                        )}
                        {st === "active" && (
                          <>
                            <ActionIcon Icon={Pause} tone="orange"
                              testId={`hq-client-service-pause-${s.service_key}`}
                              title="Pause"
                              onClick={() => patchStatus(s.bs_id, "paused")} disabled={busy}/>
                            <ActionIcon Icon={CheckCircle2} tone="blue"
                              testId={`hq-client-service-complete-${s.service_key}`}
                              title="Complete"
                              onClick={() => patchStatus(s.bs_id, "completed")} disabled={busy}/>
                            <ActionIcon Icon={XCircle} tone="red"
                              testId={`hq-client-service-cancel-${s.service_key}`}
                              title="Cancel"
                              onClick={() => patchStatus(s.bs_id, "cancelled")} disabled={busy}/>
                          </>
                        )}
                        {st === "paused" && (
                          <>
                            <ActionIcon Icon={Play} tone="green"
                              testId={`hq-client-service-resume-${s.service_key}`}
                              title="Resume"
                              onClick={() => patchStatus(s.bs_id, "active")} disabled={busy}/>
                            <ActionIcon Icon={CheckCircle2} tone="blue"
                              testId={`hq-client-service-complete-${s.service_key}`}
                              title="Complete"
                              onClick={() => patchStatus(s.bs_id, "completed")} disabled={busy}/>
                            <ActionIcon Icon={XCircle} tone="red"
                              testId={`hq-client-service-cancel-${s.service_key}`}
                              title="Cancel"
                              onClick={() => patchStatus(s.bs_id, "cancelled")} disabled={busy}/>
                          </>
                        )}
                        {st === "completed" && (
                          <ActionIcon Icon={RotateCcw} tone="grey"
                            testId={`hq-client-service-revive-${s.service_key}`}
                            title="Re-open as interested"
                            onClick={() => patchStatus(s.bs_id, "interested")} disabled={busy}/>
                        )}
                        {st === "cancelled" && (
                          <>
                            <ActionIcon Icon={RotateCcw} tone="grey"
                              testId={`hq-client-service-revive-${s.service_key}`}
                              title="Re-open as interested"
                              onClick={() => patchStatus(s.bs_id, "interested")} disabled={busy}/>
                            <ActionIcon Icon={Trash2} tone="grey"
                              testId={`hq-client-service-delete-${s.service_key}`}
                              title="Remove"
                              onClick={() => removeRow(s.bs_id)} disabled={busy}/>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
